import { ContractAnalysisJSON } from '../../types/contract.types';

export class ContractAnalysisJsonParser {
  /**
   * Parse a JSON contract analysis file
   */
  static async parseJsonAnalysis(jsonContent: string): Promise<ContractAnalysisJSON> {
    try {
      const analysis = JSON.parse(jsonContent) as ContractAnalysisJSON;
      
      // Validate the structure
      if (!analysis.executive_summary || !analysis.clauses) {
        throw new Error('Invalid JSON structure: missing executive_summary or clauses');
      }

      // Ensure all required fields are present
      this.validateExecutiveSummary(analysis.executive_summary);
      analysis.clauses.forEach(clause => this.validateClause(clause));

      return analysis;
    } catch (error) {
      if (error instanceof SyntaxError) {
        throw new Error(`Invalid JSON format: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Validate executive summary structure
   */
  private static validateExecutiveSummary(summary: any): void {
    if (!summary.overview || typeof summary.overview !== 'string') {
      throw new Error('Invalid executive summary: missing or invalid overview');
    }
    
    if (!Array.isArray(summary.critical_issues)) {
      throw new Error('Invalid executive summary: critical_issues must be an array');
    }

    if (typeof summary.overall_favorability_score !== 'number') {
      throw new Error('Invalid executive summary: overall_favorability_score must be a number');
    }

    // Validate each critical issue
    summary.critical_issues.forEach((issue: any, index: number) => {
      if (!issue.title || !issue.description || !issue.risk_level || !issue.recommended_action) {
        throw new Error(`Invalid critical issue at index ${index}: missing required fields`);
      }
    });
  }

  /**
   * Validate clause structure
   */
  private static validateClause(clause: any): void {
    const requiredFields = [
      'reference', 'title', 'full_heading', 'party_obligations',
      'risk_assessment', 'negotiation_analysis', 'actionable_recommendations', 'icons'
    ];

    requiredFields.forEach(field => {
      if (!(field in clause)) {
        throw new Error(`Invalid clause ${clause.reference || 'unknown'}: missing ${field}`);
      }
    });

    // Add fallback for summary field if missing
    if (!clause.summary) {
      clause.summary = clause.party_obligations.slice(0, 150) + '...';
    }

    // Validate arrays
    if (!Array.isArray(clause.problematic_subclauses)) {
      throw new Error(`Invalid clause ${clause.reference}: problematic_subclauses must be an array`);
    }

    // Validate nested objects
    this.validateRiskAssessment(clause.risk_assessment, clause.reference);
    this.validateNegotiationAnalysis(clause.negotiation_analysis, clause.reference);
    this.validateActionableRecommendations(clause.actionable_recommendations, clause.reference);
  }

  /**
   * Validate risk assessment structure
   */
  private static validateRiskAssessment(assessment: any, clauseRef: string): void {
    const fields = ['legal_risk', 'financial_exposure', 'operational_impact', 'regulatory_concerns'];
    fields.forEach(field => {
      if (!(field in assessment)) {
        throw new Error(`Invalid risk assessment in clause ${clauseRef}: missing ${field}`);
      }
    });
  }

  /**
   * Validate negotiation analysis structure
   */
  private static validateNegotiationAnalysis(analysis: any, clauseRef: string): void {
    const fields = ['favorability', 'market_standard', 'recommended_modification', 'classification'];
    fields.forEach(field => {
      if (!(field in analysis)) {
        throw new Error(`Invalid negotiation analysis in clause ${clauseRef}: missing ${field}`);
      }
    });

    // Validate enum values
    const validFavorability = ['favors counterparty', 'balanced', 'favors us'];
    if (!validFavorability.includes(analysis.favorability)) {
      throw new Error(`Invalid favorability value in clause ${clauseRef}: ${analysis.favorability}`);
    }

    const validClassification = ['deal-breaker', 'dealbreaker', 'negotiable', 'acceptable'];
    if (!validClassification.includes(analysis.classification)) {
      throw new Error(`Invalid classification value in clause ${clauseRef}: ${analysis.classification}`);
    }
    
    // Normalize classification to use hyphen for consistency
    if (analysis.classification === 'dealbreaker') {
      analysis.classification = 'deal-breaker';
    }
  }

  /**
   * Validate actionable recommendations structure
   */
  private static validateActionableRecommendations(recommendations: any, clauseRef: string): void {
    const fields = ['immediate_actions', 'approvals_needed', 'implementation_notes'];
    fields.forEach(field => {
      if (!(field in recommendations)) {
        throw new Error(`Invalid actionable recommendations in clause ${clauseRef}: missing ${field}`);
      }
    });
  }

  /**
   * Convert legacy analysis format to JSON format
   */
  static convertLegacyToJson(legacyAnalysis: any): ContractAnalysisJSON {
    // This method would convert the old format to the new JSON format
    // Implementation would depend on the structure of the legacy format
    throw new Error('Legacy format conversion not yet implemented');
  }

  /**
   * Load JSON analysis from a file
   */
  static async loadFromFile(file: File): Promise<ContractAnalysisJSON> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (event) => {
        try {
          const content = event.target?.result as string;
          const analysis = await this.parseJsonAnalysis(content);
          resolve(analysis);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsText(file);
    });
  }

  /**
   * Save analysis to JSON string
   */
  static stringify(analysis: ContractAnalysisJSON, pretty: boolean = true): string {
    return JSON.stringify(analysis, null, pretty ? 2 : 0);
  }
} 